import { filterClasses } from "@swivl-mono/helpers";
import { InputHTMLAttributes, forwardRef } from "react";

import { InputSize, InputVariant } from "./Input";

export interface InputFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  error?: boolean;
  hasPrefix?: boolean;
  hasSuffix?: boolean;
  inputSize?: InputSize;
  isThemed?: boolean;
  variant?: InputVariant;
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    { error, hasPrefix, hasSuffix, inputSize = InputSize.MEDIUM, isThemed, variant = InputVariant.OUTLINED, className, disabled, readOnly, ...props },
    ref
  ) => {
    const sizeClasses = {
      [InputSize.SMALL]: "h-[26px] px-3",
      [InputSize.MEDIUM]: "h-[34px] px-3"
    };
    const paddingRight = hasSuffix ? "pr-8" : "";
    const isDisabled = disabled || readOnly;

    const variantClasses = {
      [InputVariant.OUTLINED]: filterClasses(
        hasPrefix ? "rounded-r-sm border" : "rounded-sm border",
        "border-grey-400 data-[disabled=true]:border-grey-400",
        !isDisabled &&
          (error
            ? "border-red-500 hover:border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-100"
            : "hover:border-orange-500 focus:border-orange-500 focus:ring-2 focus:ring-orange-100")
      ),
      [InputVariant.UNDERLINED]: filterClasses(
        "rounded-none border-b bg-none",
        "border-grey-400 data-[disabled=true]:border-grey-400",
        !isDisabled &&
          (error
            ? "border-red-500 hover:border-red-500 focus:border-red-500 focus:shadow-[0_2px_0_0_#ffd2cc]"
            : "hover:border-orange-500 focus:border-orange-500 focus:shadow-[0_2px_0_0_#ffdcc5]")
      )
    };

    return (
      <input
        ref={ref}
        disabled={disabled || readOnly}
        data-disabled={isDisabled || undefined}
        className={filterClasses(
          "body-sm w-full caret-orange-500 transition-[border-color,box-shadow] duration-200 ease-in-out outline-none",
          "bg-linear-to-b from-grey-50 via-white via-[22.12%] to-white text-black placeholder:text-grey-600",
          "data-[disabled=true]:cursor-not-allowed data-[disabled=true]:bg-linear-to-b data-[disabled=true]:from-grey-200 data-[disabled=true]:via-grey-200 data-[disabled=true]:to-grey-200 data-[disabled=true]:text-grey-500 data-[disabled=true]:placeholder:text-grey-500",
          variantClasses[variant],
          sizeClasses[inputSize],
          paddingRight,
          className
        )}
        {...props}
      />
    );
  }
);

InputField.displayName = "InputField";
